import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Phone, Mail, MapPin, Clock, ArrowRight, CheckCircle, AlertCircle } from 'lucide-react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { useBrand } from '../context/BrandContext';
import useAntiBot from '../hooks/useAntiBot';
import useRecaptcha from '../hooks/useRecaptcha';
import { trackEvent } from '../utils/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const subjects = [
  'General Enquiry',
  'Existing Loan Query',
  'Partnership Enquiry',
  'Media Enquiry',
  'Other',
];

const contactInfo = [
  {
    icon: Phone,
    label: 'Phone',
    value: '0468 007 056',
    href: 'tel:+61468007056',
  },
  {
    icon: Mail,
    label: 'Email',
    value: 'admin@manageyourloans.com.au',
    href: 'mailto:admin@manageyourloans.com.au',
  },
  {
    icon: MapPin,
    label: 'Office',
    value: 'Level 1, 18-20 Knuckey St, Darwin NT 0800',
    href: 'https://maps.google.com/?q=Level+1,+18-20+Knuckey+St,+Darwin+NT+0800',
  },
  {
    icon: Clock,
    label: 'Hours',
    value: 'Mon-Fri 9am-5pm AEST',
    href: null,
  },
];

const ContactUs = () => {
  const brand = useBrand();
  const source = brand.source ? brand.source.replace('MYL', 'MYL-Contact').replace('Costco', 'Costco-Contact') : 'MYL-Contact';

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const { honeypotProps, getAntiBotPayload, validateSubmission } = useAntiBot();
  const { getRecaptchaToken } = useRecaptcha();
  const panelRef = useRef(null);
  const resetTimerRef = useRef(null);

  // Don't leave a timer running against an unmounted form.
  useEffect(() => () => clearTimeout(resetTimerRef.current), []);

  // The thank-you panel is much shorter than the form it replaces, so the page
  // collapses while the browser keeps the old scroll offset — leaving the
  // confirmation off-screen. Pull it into view so it's actually seen.
  useEffect(() => {
    if (submitted) {
      panelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [submitted]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'phone') {
      const digits = value.replace(/[^0-9]/g, '');
      setFormData((prev) => ({ ...prev, [name]: digits }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const botCheck = validateSubmission();
    if (!botCheck.ok) {
      if (botCheck.silent) {
        setSubmitted(true);
        return;
      }
      setError(botCheck.error);
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const recaptchaToken = await getRecaptchaToken('submit_contact');

      const payload = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        loanType: formData.subject,
        message: formData.message,
        source,
        ...getAntiBotPayload(),
        recaptchaToken,
      };

      const response = await axios.post(`${BACKEND_URL}/api/leads`, payload);

      if (response.data.status === 'success') {
        trackEvent('form_submit', { form_type: 'contact', source });
        setSubmitted(true);
        resetTimerRef.current = setTimeout(() => {
          // Return the visitor to the top of the page rather than snapping the
          // form back under them, then restore a clean form behind them.
          window.scrollTo({ top: 0, behavior: 'smooth' });
          setSubmitted(false);
          setFormData({
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            subject: '',
            message: '',
          });
        }, 5000);
      }
    } catch (err) {
      console.error('Contact form submission failed:', err);
      const detail = err.response?.data?.detail;
      const message = typeof detail === 'string' ? detail : 'Something went wrong. Please try again or call us on 0468 007 056.';
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Contact Us"
        description="Contact Manage Your Loans. Call 0468 007 056, email admin@manageyourloans.com.au, or visit our Darwin office."
        path="/contact"
      />
      <Navigation />

      {/* Hero */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-[#00594C] to-[#003d35]">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Get In Touch</h1>
          <p className="text-lg text-gray-200 max-w-2xl mx-auto">
            Have a question or ready to explore your options? We'd love to hear from you.
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-10">

            {/* LEFT: Contact Info */}
            <div className="lg:col-span-2 space-y-5">
              <h2 className="text-2xl font-semibold text-[#00594C] mb-6">Contact Information</h2>
              {contactInfo.map((item) => {
                const Icon = item.icon;
                const content = (
                  <div className="flex items-start space-x-4 bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                    <div className="w-12 h-12 bg-[#99DF63]/20 rounded-full flex items-center justify-center flex-shrink-0">
                      <Icon className="w-6 h-6 text-[#00594C]" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500 mb-1">{item.label}</p>
                      <p className="text-[#00594C] font-semibold">{item.value}</p>
                    </div>
                  </div>
                );

                return item.href ? (
                  <a
                    key={item.label}
                    href={item.href}
                    target={item.icon === MapPin ? '_blank' : undefined}
                    rel={item.icon === MapPin ? 'noopener noreferrer' : undefined}
                    className="block"
                  >
                    {content}
                  </a>
                ) : (
                  <div key={item.label}>{content}</div>
                );
              })}
            </div>

            {/* RIGHT: Contact Form */}
            <div className="lg:col-span-3">
              {submitted ? (
                <div ref={panelRef} className="bg-white rounded-2xl p-12 shadow-sm text-center">
                  <div className="w-16 h-16 bg-[#99DF63]/20 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="w-8 h-8 text-[#00594C]" />
                  </div>
                  <h3 className="text-2xl font-semibold text-[#00594C] mb-4">
                    Thank you for your enquiry!
                  </h3>
                  <p className="text-gray-600">
                    Thanks for your enquiry, one of our Brokers will be in touch soon.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-no-track className="bg-white rounded-2xl p-8 shadow-sm space-y-6">
                  {honeypotProps.field}
                  <h2 className="text-2xl font-semibold text-[#00594C] mb-2">Send Us a Message</h2>

                  {/* Name Row */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                      <input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#99DF63] focus:border-[#00594C] outline-none transition-colors"
                        placeholder="John"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                      <input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#99DF63] focus:border-[#00594C] outline-none transition-colors"
                        placeholder="Smith"
                      />
                    </div>
                  </div>

                  {/* Contact Row */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#99DF63] focus:border-[#00594C] outline-none transition-colors"
                        placeholder="john.smith@email.com"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        pattern="04[0-9]{8}"
                        title="Australian mobile number starting with 04"
                        maxLength={12}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#99DF63] focus:border-[#00594C] outline-none transition-colors"
                        placeholder="04XX XXX XXX"
                      />
                    </div>
                  </div>

                  {/* Subject */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Subject *</label>
                    <select
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#99DF63] focus:border-[#00594C] outline-none transition-colors bg-white"
                    >
                      <option value="">Select a subject</option>
                      {subjects.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>

                  {/* Message */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Message *</label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#99DF63] focus:border-[#00594C] outline-none transition-colors resize-none"
                      placeholder="How can we help you?"
                    />
                  </div>

                  {/* Error */}
                  {error && (
                    <div className="flex items-center space-x-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                      <AlertCircle className="w-5 h-5 flex-shrink-0" />
                      <span>{error}</span>
                    </div>
                  )}

                  {/* Submit */}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full flex items-center justify-center space-x-2 bg-[#99DF63] text-[#00594C] px-8 py-4 rounded-full font-bold text-lg hover:bg-[#8BCF52] transition-all shadow-lg hover:shadow-xl transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                  >
                    <span>{isSubmitting ? 'Sending...' : 'Send Message'}</span>
                    {!isSubmitting && <ArrowRight className="w-5 h-5" />}
                  </button>

                  {/* Privacy */}
                  <p className="text-center text-gray-500 text-sm">
                    By submitting this form, you agree to our{' '}
                    <a href="/privacy-policy" className="text-[#00594C] hover:underline">Privacy Policy</a> and{' '}
                    <a href="/terms-of-use" className="text-[#00594C] hover:underline">Terms of Use</a>.
                  </p>
                </form>
              )}
            </div>

          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactUs;
